import { useEffect, useMemo, useState } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import {
  Plus,
  Wand2,
  Download,
  Upload,
  LayoutList,
  Columns3,
  CalendarDays,
  Loader2,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { shotsApi } from "@/lib/api";
import ShotTable, { compareShots } from "@/components/ShotTable";
import ShotBoard from "@/components/ShotBoard";
import ShotCalendar from "@/components/ShotCalendar";
import CsvImportDialog from "@/components/CsvImportDialog";

const SORT_STORAGE_KEY = "shots-sort";

function readInitialSort() {
  try {
    const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.key && (parsed.dir === "asc" || parsed.dir === "desc")) {
      return parsed;
    }
  } catch {
    /* ignore corrupt state */
  }
  return null;
}

export default function ProjectShotsPage() {
  const { id } = useParams();
  const { project } = useOutletContext();
  const canEdit = project?.my_role === "admin" || project?.my_role === "editor";
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiLoading, setAiLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Smart Persistent Sort — shared across all three view tabs (Table / Board /
  // Calendar) and persisted across navigations via localStorage so producers
  // don't lose their focus (e.g. "sort by Location") when jumping around.
  const [sort, setSort] = useState(readInitialSort);
  useEffect(() => {
    try {
      if (sort) window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
      else window.localStorage.removeItem(SORT_STORAGE_KEY);
    } catch {
      /* private mode — no-op */
    }
  }, [sort]);

  // The single source of truth for row order across all three views.
  // Board (Tab 2) uses it to order rows within each status column.
  // Calendar (Tab 3) already groups by date first; passing sortedShots
  // here means the secondary order within each date group matches Tab 1.
  const sortedShots = useMemo(() => {
    if (!sort) return shots;
    const copy = shots.slice();
    copy.sort((a, b) => compareShots(a, b, sort.key, sort.dir));
    return copy;
  }, [shots, sort]);

  const reload = async () => {
    const sh = await shotsApi.list(id);
    setShots(sh);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await reload();
      } catch {
        toast.error("Failed to load shots");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const stats = useMemo(() => {
    const total = shots.length;
    const completed = shots.filter((s) => s.status === "completed").length;
    const inProgress = shots.filter((s) => s.status === "in_progress").length;
    return { total, completed, inProgress, notShot: total - completed - inProgress };
  }, [shots]);

  const handleAddShot = async () => {
    try {
      const newShot = await shotsApi.create(id, {
        scene: "",
        shot_number: String(shots.length + 1),
        description: "",
      });
      setShots((s) => [...s, newShot]);
    } catch {
      toast.error("Failed to add shot");
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Describe the scene first");
      return;
    }
    setAiLoading(true);
    try {
      const res = await shotsApi.aiSuggest(id, aiPrompt.trim(), aiCount);
      await reload();
      toast.success(`Generated ${res.count} shots`);
      setAiPrompt("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (shots.length === 0) {
      toast.error("No shots to export");
      return;
    }
    const cols = [
      "scene",
      "shot_number",
      "description",
      "shot_size",
      "camera_angle",
      "lens",
      "location",
      "talent",
      "props",
      "duration",
      "status",
      "scheduled_date",
      "notes",
    ];
    const esc = (v) => {
      const s = String(v ?? "");
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const csv = [
      cols.join(","),
      ...shots.map((s) => cols.map((c) => esc(s[c])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(project?.name || "shotlist").replace(/\s+/g, "_")}_shots.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8 space-y-8">
      {/* Stats + action bar */}
      <div
        data-testid="shots-toolbar"
        className="flex items-center justify-between gap-3 flex-wrap"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Stat label="Shots" value={stats.total} />
          <Stat label="In Progress" value={stats.inProgress} accent="amber" />
          <Stat label="Completed" value={stats.completed} accent="emerald" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <button
              data-testid="import-csv-btn"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm transition-colors"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
          )}
          <button
            data-testid="export-csv-btn"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <Link
            to={`/projects/${id}/call-sheet`}
            data-testid="open-call-sheet-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm transition-colors"
          >
            <ClipboardList className="w-4 h-4" /> Call Sheet
          </Link>
        </div>
      </div>

      {/* AI Generator (editor+) */}
      {canEdit && (
        <section
        data-testid="ai-generator-section"
        className="rounded-md bg-zinc-900/60 border border-amber-500/20 p-6 ai-glow"
      >
        <div className="flex items-center gap-3 mb-4">
          <Wand2 className="w-5 h-5 text-amber-500" />
          <div>
            <div className="font-heading text-lg text-zinc-100">
              AI Shot Generator
            </div>
            <div className="text-xs text-zinc-500">
              Describe the scene — Claude crafts a structured shot list.
            </div>
          </div>
        </div>
        <Textarea
          data-testid="ai-prompt-input"
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          placeholder="e.g., A young woman walks through a neon-lit Tokyo alley at night, her face wet from rain. She stops to read a note left on her door..."
          className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[100px] font-body"
        />
        <div className="flex items-center justify-between gap-4 mt-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Label htmlFor="ai-num" className="text-overline text-zinc-500">
              Shots
            </Label>
            <Input
              id="ai-num"
              data-testid="ai-num-shots"
              type="number"
              min={1}
              max={12}
              value={aiCount}
              onChange={(e) => setAiCount(parseInt(e.target.value) || 5)}
              className="w-20 bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <button
            data-testid="generate-ai-shots-btn"
            onClick={handleAiGenerate}
            disabled={aiLoading}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {aiLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Directing…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" /> Generate Shots
              </>
            )}
          </button>
        </div>
      </section>
      )}

      {/* View toggle + Add */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div
          className="inline-flex rounded-sm border border-zinc-800 bg-zinc-900 p-0.5"
          role="tablist"
        >
          <ViewTab
            active={view === "table"}
            onClick={() => setView("table")}
            icon={LayoutList}
            label="List"
            testId="view-table"
          />
          <ViewTab
            active={view === "board"}
            onClick={() => setView("board")}
            icon={Columns3}
            label="Board"
            testId="view-board"
          />
          <ViewTab
            active={view === "calendar"}
            onClick={() => setView("calendar")}
            icon={CalendarDays}
            label="Calendar"
            testId="view-calendar"
          />
        </div>
        {canEdit && (
          <button
            data-testid="add-shot-btn"
            onClick={handleAddShot}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-zinc-900 border border-zinc-800 text-zinc-100 hover:bg-zinc-800 rounded-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Shot
          </button>
        )}
      </div>

      {view === "table" && (
        <ShotTable
          projectId={id}
          shots={shots}
          setShots={setShots}
          onReload={reload}
          readOnly={!canEdit}
          sort={sort}
          onSortChange={setSort}
        />
      )}
      {view === "board" && (
        <ShotBoard shots={sortedShots} setShots={setShots} onReload={reload} readOnly={!canEdit} />
      )}
      {view === "calendar" && (
        <ShotCalendar shots={sortedShots} setShots={setShots} onReload={reload} readOnly={!canEdit} />
      )}

      <CsvImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectId={id}
        onImported={reload}
      />
    </div>
  );
}

function Stat({ label, value, accent }) {
  const accentCls =
    accent === "amber"
      ? "text-amber-500"
      : accent === "emerald"
        ? "text-emerald-500"
        : "text-zinc-100";
  return (
    <div className="px-3 py-1.5 border border-zinc-800 rounded-sm bg-zinc-900/60">
      <div className="text-overline text-zinc-500">{label}</div>
      <div className={`text-base font-mono font-semibold ${accentCls}`}>{value}</div>
    </div>
  );
}

function ViewTab({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-1.5 text-sm rounded-sm transition-all ${
        active
          ? "bg-zinc-950 text-amber-500 border border-amber-500/30"
          : "text-zinc-400 hover:text-zinc-100"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
